import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  Button, Stack, Table, TableHead, TableBody, TableRow, TableCell, Chip, Typography,
} from '@mui/material'
import * as XLSX from 'xlsx'

import { defaultFunction, getFullName } from '../../utilities/utilities'

import './PendingInvitationsModal.sass'

function formatScheduledTime(invitation) {
  if (invitation.invitation_time) {
    const parsed = new Date(invitation.invitation_time)
    if (!isNaN(parsed.getTime())) return parsed.toLocaleString()
  }
  return invitation.date || invitation.invitation_date || '—'
}

/**
 * Analogo a `PendingVisitsModal` pero para invitaciones/pre-registros:
 * mostrado desde `LoadPage` cuando `quarantined_invitations` no esta vacio,
 * es decir, la sesion anterior termino (logout) con invitaciones que nunca
 * fueron confirmadas por el servidor (ver `clearLocalDatabase` en db.js).
 *
 * El usuario debe elegir explicitamente resincronizar o descartarlas; no hay
 * cierre por backdrop/escape ya que representa pre-registros reales que de
 * otro modo desaparecerian silenciosamente.
 */
function PendingInvitationsModal(props) {
  const {
    open = false,
    invitations = [],
    onSync = defaultFunction,
    onDiscard = defaultFunction,
  } = props

  const { t } = useTranslation()
  const [processing, setProcessing] = useState(false)

  const handleSync = async () => {
    setProcessing(true)
    try {
      await onSync()
    } finally {
      setProcessing(false)
    }
  }

  const handleDiscard = async () => {
    setProcessing(true)
    try {
      await onDiscard()
    } finally {
      setProcessing(false)
    }
  }

  const handleDownload = () => {
    const headers = [
      t('Fullname'),
      t('Identification'),
      t('email'),
      t('Go to'),
      t('Comes from'),
      t('pending_invitations_modal_col_scheduled_time'),
      t('preregistration_reception_notes'),
      t('sync_status'),
      t('pending_visits_modal_col_sync_error'),
    ]
    const body = invitations.map(invitation => [
      [invitation.first_name, invitation.last_name_one, invitation.last_name_two].filter(Boolean).join(' '),
      invitation.identification || '',
      invitation.email || '',
      invitation.destination?.name || '',
      invitation.comes_from || '',
      formatScheduledTime(invitation),
      invitation.notes || '',
      invitation.syncStatus === 'failed' ? t('sync_failed') : t('sync_pending'),
      invitation.syncError || '',
    ])

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...body])
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'pending_invitations')

    const date = new Date().toISOString().slice(0, 10)
    XLSX.writeFile(workbook, `pending_invitations_${date}.xlsx`)
  }

  return (
    <Dialog
      className="pending-invitations-modal"
      open={open}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
    >
      <DialogTitle>{t('pending_invitations_modal_title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('pending_invitations_modal_description', { count: invitations.length })}
        </DialogContentText>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('Fullname')}</TableCell>
              <TableCell>{t('Identification')}</TableCell>
              <TableCell>{t('pending_invitations_modal_col_scheduled_time')}</TableCell>
              <TableCell>{t('sync_status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invitations.map(invitation => (
              <TableRow key={invitation.id}>
                <TableCell>{getFullName(invitation)}</TableCell>
                <TableCell>{invitation.identification}</TableCell>
                <TableCell>{formatScheduledTime(invitation)}</TableCell>
                <TableCell>
                  <Stack spacing={0.5}>
                    <Chip
                      size="small"
                      label={invitation.syncStatus === 'failed' ? t('sync_failed') : t('sync_pending')}
                      color={invitation.syncStatus === 'failed' ? 'error' : 'warning'}
                      variant="outlined"
                    />
                    {invitation.syncStatus === 'failed' && invitation.syncError && (
                      <Typography variant="caption" color="error">
                        {invitation.syncError}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <DialogContentText className="pending-invitations-modal-discard-warning">
          {t('pending_invitations_modal_discard_warning')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={handleDownload}
          disabled={processing || invitations.length === 0}
          sx={{ mr: 'auto' }}
        >
          {t('Download')}
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={handleDiscard}
          disabled={processing}
        >
          {t('pending_visits_modal_discard_button')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSync}
          disabled={processing}
        >
          {t('pending_visits_modal_sync_button')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PendingInvitationsModal
